import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { LinkedinTypes } from 'enums';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiHelpIcon from '../../ui/base-components/pai-help-icon';
import PaiAutoComplete, { OptionType } from '../../ui/base-components/pai-auto-complete';
import PaiButton from '../../ui/base-components/pai-button';
import PaiRadioItem from '../../ui/base-components/pai-radio-item';
import { CountryCode } from './linkedin-accounts.types';

interface Props {
  open: boolean;
  allCountryCodes: CountryCode[];
  selectedCountry: OptionType;
  selectedCity: OptionType;
  linkedinProduct: LinkedinTypes;
  loading: boolean;

  handleCountryChange(event: React.ChangeEvent<any>, newValue: any): void;

  handleCityChange(event: React.ChangeEvent<any>, newValue: any): void;

  handleLinkedinProductChange(value: string): () => void;

  handleClose(): void;

  handleEditLinkedinAccount(): void;
}

const EditLinkedinAccountGeneralSettingsDialog: React.FC<Props> = ({
  open,
  allCountryCodes = [],
  selectedCountry,
  selectedCity,
  linkedinProduct,
  loading,
  handleCountryChange,
  handleCityChange,
  handleLinkedinProductChange,
  handleClose,
  handleEditLinkedinAccount,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const linkedinProducts = [
    { label: 'LinkedIn', value: LinkedinTypes.LINKEDIN },
    { label: 'LinkedIn + Sales Navigator', value: LinkedinTypes.SALES_NAV },
    { label: 'LinkedIn + Recruiter', value: LinkedinTypes.RECRUITER },
  ];

  const countryCodeOptions: OptionType[] = allCountryCodes.map((countryCode) => ({ label: countryCode.name, value: countryCode.code }));

  const getCitiesOfCountry = () => {
    if (!selectedCountry?.value) return [];

    const foundCountry = allCountryCodes.find((country) => country.code === selectedCountry.value);

    if (!_.isNil(foundCountry) && foundCountry.cities?.options) {
      return foundCountry.cities.options.map((cityCode) => ({ label: cityCode.name, value: cityCode.code }));
    } else {
      return [];
    }
  };

  const cityCodeOptions: OptionType[] = getCitiesOfCountry();

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'xs'} transitionDuration={0}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('editLinkedinAccount')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={4}>
          {/* Linkedin product */}
          <Stack gap={1}>
            <Typography className={classes.formTitle}>{`* ${t('selectLinkedinProduct')}`}</Typography>
            <Typography className={classes.clarificationText}>{t('selectLinkedinProductClarification')}</Typography>
            <Stack direction={'column'} gap={1} pl={'8px'}>
              {linkedinProducts.map((product) => (
                <PaiRadioItem
                  key={product.value}
                  label={product.label}
                  value={product.value}
                  selectedValue={linkedinProduct}
                  variant="row"
                  handleSelectItem={handleLinkedinProductChange}
                />
              ))}
            </Stack>
          </Stack>

          <Divider />

          {/* Proxy information */}
          <Stack gap={1}>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <Typography className={classes.formTitle}>{`* ${t('proxyServerInformation')}`}</Typography>
              <PaiHelpIcon tooltipText={t('proxyServerInformationDescription')} />
            </Stack>
            <Typography className={classes.clarificationText}>{t('proxyServerClarification')}</Typography>
            <PaiAutoComplete
              options={countryCodeOptions}
              selected={selectedCountry || { label: '', value: '' }}
              placeholder={t('country')}
              disableClearable={true}
              onChange={handleCountryChange}
            />
            <PaiAutoComplete
              options={cityCodeOptions}
              selected={selectedCity || { label: '', value: '' }}
              placeholder={t('city')}
              disableClearable={true}
              disabled={!selectedCountry?.value}
              onChange={handleCityChange}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton
          text={t('ok')}
          size={'small'}
          border={'rounded'}
          action={handleEditLinkedinAccount}
          loading={loading}
          disabled={!selectedCountry?.value || !selectedCity?.value || !linkedinProduct}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    padding: '20px',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  formTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  clarificationText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.primary.main,
    marginBottom: '4px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default EditLinkedinAccountGeneralSettingsDialog;
